import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { toast } from "sonner";
import {
  AlertCircle,
  Bell,
  CheckCircle,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Loader2,
  Lock,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  apiGetMyWorkflowTasks,
  apiCompleteWorkflowTask,
  apiGetNotifications,
  apiGetNotificationUnreadCount,
  apiMarkNotificationRead,
  apiMarkAllNotificationsRead,
  apiGetEmployeeSurveys,
  apiGetSurvey,
  type WorkflowTask,
  type AppNotification,
  type Survey,
  type SurveyDetail,
} from "@/lib/api";
import { getNotificationDisplayText } from "@/lib/notification-display";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { mobileNotificationDestination, invalidateNotificationQueries } from "@/lib/mobile-notifications";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SurveyAnswerSheet } from "@/components/mobile/SurveyAnswerSheet";

// ── Task card ────────────────────────────────────────────────────────────────
function TaskCard({ task }: { task: WorkflowTask }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const qc = useQueryClient();

  const completeMutation = useMutation({
    mutationFn: (outcome: string) =>
      apiCompleteWorkflowTask(task.id, { outcome }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-workflow-tasks"] });
      qc.invalidateQueries({ queryKey: ["mobile-task-count"] });
      toast.success(t("mobile.tasks.completed"));
    },
    onError: () => toast.error(t("mobile.tasks.completeFailed")),
  });

  const isApproval =
    task.task_type === "approval" || task.step_type === "approval";

  return (
    <div id={`mobile-task-${task.id}`} tabIndex={-1} className="bg-card border border-border/60 rounded-xl p-4 space-y-3 focus-visible:ring-2 focus-visible:ring-ring">
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-foreground leading-tight flex-1">
            {task.title}
          </p>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {task.task_type}
          </Badge>
        </div>
        {task.workflow_name && (
          <p className="text-xs text-muted-foreground">{task.workflow_name}</p>
        )}
        {task.description && (
          <p className="text-xs text-foreground/70 line-clamp-2">{task.description}</p>
        )}
        {task.due_date && (
          <p className="text-xs text-muted-foreground">
            {t("mobile.tasks.due")}:{" "}
            {formatDistanceToNow(new Date(task.due_date), { addSuffix: true, locale: dateLocale })}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        {isApproval ? (
          <>
            <Button
              size="sm"
              className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={completeMutation.isPending}
              onClick={() => completeMutation.mutate("approved")}
            >
              <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.tasks.approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-9 text-destructive border-destructive/40 hover:bg-destructive/10"
              disabled={completeMutation.isPending}
              onClick={() => completeMutation.mutate("rejected")}
            >
              <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.tasks.reject")}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-9"
            disabled={completeMutation.isPending}
            onClick={() => completeMutation.mutate("completed")}
          >
            <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
            {t("mobile.tasks.markComplete")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Notification row ──────────────────────────────────────────────────────────
function NotificationRow({ notif }: { notif: AppNotification }) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const navigate = useNavigate();
  const display = getNotificationDisplayText(notif, t);

  const markRead = useMutation({
    mutationFn: () => apiMarkNotificationRead(notif.id),
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });

  function handleClick() {
    if (!notif.isRead) markRead.mutate();
    navigate(mobileNotificationDestination(notif));
  }

  return (
    <button
      type="button"
      className={cn(
        "w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border/30 last:border-0 transition-colors cursor-pointer",
        !notif.isRead && "bg-primary/5"
      )}
      onClick={handleClick}
    >
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <Bell className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground leading-tight">{display.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{display.message}</p>
        <p className="text-[10px] text-muted-foreground/70 mt-1">
          {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true, locale: dateLocale })}
        </p>
      </div>
      {!notif.isRead && (
        <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
      )}
    </button>
  );
}

// ── Survey card ──────────────────────────────────────────────────────────────
function formatDueDate(date: string, t: (key: string, opts?: any) => string, language: string) {
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86400000);
  if (diffDays < 0) return { label: t("mobile.inbox.surveyOverdue"), urgent: true };
  if (diffDays === 0) return { label: t("mobile.inbox.surveyDueToday"), urgent: true };
  if (diffDays <= 7) return { label: t("mobile.inbox.surveyDueInDays", { count: diffDays }), urgent: true };
  return { label: d.toLocaleDateString(language.startsWith("mk") ? "mk-MK" : "en-GB", { month: "short", day: "numeric" }), urgent: false };
}

function SurveyCard({ survey, onClick, loading }: { survey: Survey; onClick: () => void; loading: boolean }) {
  const { t, i18n } = useTranslation();
  const due = survey.endDate ? formatDueDate(survey.endDate, t, i18n.language) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left disabled:opacity-60"
    >
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ClipboardList className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground truncate">{survey.title}</p>
          {survey.isAnonymous && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>{survey.questionCount} {t("surveys.questions")}</span>
          {due && (
            <>
              <span className="text-border/60">·</span>
              <span className={due.urgent ? "text-amber-600 font-medium flex items-center gap-1" : ""}>
                {due.urgent && <AlertCircle className="h-3 w-3" />}
                {due.label}
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MobileInbox() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const { canAccess } = useModuleAccess();
  const requestedTab = searchParams.get("tab");
  const activeTab = requestedTab === "tasks" && canAccess("myTasks") ? "tasks"
    : requestedTab === "surveys" && canAccess("mySurveys") ? "surveys" : "notifications";
  const navigate = useNavigate();

  const { data: tasks = [] } = useQuery({
    queryKey: ["mobile-workflow-tasks"],
    queryFn: apiGetMyWorkflowTasks,
    enabled: canAccess("myTasks"),
    staleTime: 2 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  const { data: notifData } = useQuery({
    queryKey: ["mobile-inbox-notifications"],
    queryFn: () => apiGetNotifications(30, 0),
    staleTime: 60_000,
  });

  const { data: surveys = [] } = useQuery({
    queryKey: ["mobile-inbox-surveys"],
    queryFn: apiGetEmployeeSurveys,
    enabled: canAccess("mySurveys"),
    staleTime: 2 * 60_000,
  });

  const [activeSurvey, setActiveSurvey] = useState<SurveyDetail | null>(null);
  const [loadingSurveyId, setLoadingSurveyId] = useState<number | null>(null);

  const openSurvey = async (s: Survey) => {
    if (s.hasResponded || loadingSurveyId !== null) return;
    setLoadingSurveyId(s.id);
    try {
      const full = await apiGetSurvey(s.id, "mine");
      setActiveSurvey(full);
    } catch {
      toast.error(t("mobile.teamLeave.actionFailed"));
    } finally {
      setLoadingSurveyId(null);
    }
  };

  const markAllRead = useMutation({
    mutationFn: apiMarkAllNotificationsRead,
    onSuccess: () => {
      invalidateNotificationQueries(qc);
      toast.success(t("mobile.inbox.allRead"));
    },
  });

  const pendingTasks = tasks.filter(
    (t) => t.status === "pending" && !t.is_archived
  );
  const notifications = notifData?.notifications ?? [];
  const { data: unread } = useQuery({ queryKey: ["mobile-notif-count"], queryFn: apiGetNotificationUnreadCount, staleTime: 60_000 });
  const unreadCount = unread?.count ?? notifData?.unreadCount ?? 0;
  const selectedNotification = notifications.find(n => String(n.id) === searchParams.get("notification"));
  const selectedDisplay = selectedNotification ? getNotificationDisplayText(selectedNotification, t) : null;
  const pendingSurveys = surveys.filter((s) => !s.hasResponded);
  const completedSurveys = surveys.filter((s) => s.hasResponded);

  useEffect(() => {
    if (activeTab !== "tasks") return;
    const taskId = searchParams.get("taskId");
    if (!taskId) return;
    const element = document.getElementById(`mobile-task-${taskId}`);
    element?.scrollIntoView?.({ block: "nearest" });
    element?.focus({ preventScroll: true });
  }, [activeTab, searchParams, tasks]);

  return (
    <div className="flex flex-col h-full">
      <Tabs value={activeTab} onValueChange={tab => navigate(`/mobile/inbox?tab=${tab}`)} className="flex flex-col flex-1 overflow-hidden">
        {/* Tab header */}
        <div className="px-4 pt-4 pb-2 shrink-0">
          <TabsList className="flex w-full [&>button]:flex-1">
            {canAccess("myTasks") && <TabsTrigger value="tasks" className="text-xs">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.inbox.tasks")}
              {pendingTasks.length > 0 && (
                <Badge className="ml-1.5 h-4 min-w-4 px-1 text-[10px]" variant="default">
                  {pendingTasks.length}
                </Badge>
              )}
            </TabsTrigger>}
            <TabsTrigger value="notifications" className="text-xs">
              <Bell className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.inbox.notifications")}
              {unreadCount > 0 && (
                <Badge className="ml-1.5 h-4 min-w-4 px-1 text-[10px]" variant="destructive">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            {canAccess("mySurveys") && <TabsTrigger value="surveys" className="text-xs">
              <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.inbox.surveys")}
              {pendingSurveys.length > 0 && (
                <Badge className="ml-1.5 h-4 min-w-4 px-1 text-[10px]" variant="default">
                  {pendingSurveys.length}
                </Badge>
              )}
            </TabsTrigger>}
          </TabsList>
        </div>

        {/* Tasks tab */}
        {canAccess("myTasks") && <TabsContent value="tasks" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {pendingTasks.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <CheckCircle className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("mobile.inbox.noTasks")}</p>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {pendingTasks.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>}

        {/* Notifications tab */}
        <TabsContent value="notifications" className="flex-1 overflow-y-auto mt-0 flex flex-col data-[state=inactive]:hidden">
          {/* Mark all read header */}
          {unreadCount > 0 && (
            <div className="px-4 pt-2 pb-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-muted-foreground"
                disabled={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                <X className="h-3 w-3 mr-1" />
                {t("mobile.inbox.markAllRead")}
              </Button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                <Bell className="h-10 w-10 opacity-30" />
                <p className="text-sm">{t("mobile.inbox.noNotifications")}</p>
              </div>
            ) : (
              <div className="bg-card border border-border/60 mx-4 my-2 rounded-xl overflow-hidden">
                {notifications.map((n) => (
                  <NotificationRow key={n.id} notif={n} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Surveys tab */}
        {canAccess("mySurveys") && <TabsContent value="surveys" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {surveys.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <ClipboardCheck className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("surveys.noSurveysAvailable")}</p>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              {pendingSurveys.length > 0 && (
                <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
                  {pendingSurveys.map((s) => (
                    <SurveyCard key={s.id} survey={s} onClick={() => openSurvey(s)} loading={loadingSurveyId === s.id} />
                  ))}
                </div>
              )}
              {completedSurveys.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">
                    {t("surveys.completedSurveys")}
                  </p>
                  <div className="bg-card border border-border/40 rounded-xl overflow-hidden opacity-70">
                    {completedSurveys.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0">
                        <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 shrink-0">
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground/80 truncate">{s.title}</p>
                          <p className="text-xs text-muted-foreground">{t("surveys.alreadyCompleted")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>}
      </Tabs>

      <Dialog open={!!selectedDisplay} onOpenChange={open => { if (!open) navigate("/mobile/inbox?tab=notifications", { replace: true }); }}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedDisplay?.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap break-words">{selectedDisplay?.message}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      <SurveyAnswerSheet
        survey={activeSurvey}
        onOpenChange={(open) => { if (!open) setActiveSurvey(null); }}
      />
    </div>
  );
}
